import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Eye, FileText } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { saltFormationColors, doubleDisplacementReactions } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Setting Up Test Tubes',
    content: 'Double displacement (metathesis) reactions occur when two ionic compounds exchange partners. A precipitate forms if the new combination produces an insoluble compound.',
    whatHappens: 'Five test tubes are labeled and prepared for the double displacement reactions we will test.',
    observe: ['Label tubes 1-5', 'Each tube will test a different combination', 'Have all solutions ready']
  },
  2: {
    title: 'Reaction 1: NaCl + KNO3',
    content: 'When NaCl and KNO3 are mixed, the possible new combinations are NaNO₃ and KCl. Both of these are soluble according to solubility rules (all nitrates and most chlorides are soluble).',
    whatHappens: 'Mixing these two solutions results in no visible change - all four ionic species remain dissolved.',
    observe: ['No precipitate forms', 'Solution remains clear', 'All products are soluble'],
    equation: 'NaCl(aq) + KNO3(aq) -> NaNO₃(aq) + KCl(aq) (all soluble)'
  },
  3: {
    title: 'Observing Reaction 1',
    content: 'This demonstrates that not all double displacement reactions produce precipitates. A reaction is considered to "go forward" only if a precipitate, gas, or water forms.',
    whatHappens: 'Confirming that no precipitate formed - this is a case where no net reaction occurs.',
    observe: ['Solution stays clear', 'No color change', 'This is called "no reaction"']
  },
  4: {
    title: 'Reaction 2: CuSO₄ + Na₂S',
    content: 'Copper(II) sulfide (CuS) is very insoluble (Ksp = 6 x 10^-37). When Cu²⁺ meets S2-, they immediately form a black precipitate. Most metal sulfides are insoluble.',
    whatHappens: 'A dramatic black precipitate of CuS forms immediately upon mixing.',
    observe: ['Black precipitate forms instantly', 'CuS is highly insoluble', 'Blue Cu²⁺ color disappears'],
    equation: 'CuSO₄(aq) + Na₂S(aq) -> CuS(s) + Na₂SO₄(aq)'
  },
  5: {
    title: 'Observing Reaction 2',
    content: 'The black color of CuS is due to the electronic structure of Cu²⁺ bonded to S2-. This is a characteristic test for sulfide ions or copper ions.',
    whatHappens: 'The black precipitate confirms that Cu²⁺ and S2- form an insoluble compound.',
    observe: ['Dense black precipitate', 'Settles to bottom of tube', 'Clear solution above']
  },
  6: {
    title: 'Reaction 3: CaCl₂ + Na₂CO₃',
    content: 'Calcium carbonate (CaCO₃) is insoluble - it is the mineral calcite and the main component of limestone, chalk, and seashells. Most carbonates are insoluble except Group 1 and NH4+.',
    whatHappens: 'A white precipitate of CaCO₃ forms when Ca²⁺ meets CO32-.',
    observe: ['White precipitate forms', 'CaCO₃ is chalk/limestone', 'Milky white appearance'],
    equation: 'CaCl₂(aq) + Na₂CO₃(aq) -> CaCO₃(s) + 2NaCl(aq)'
  },
  7: {
    title: 'Observing Reaction 3',
    content: 'Calcium carbonate has many important uses: as a building material (limestone), in antacids, and as a calcium supplement. It dissolves in acid with CO2 evolution.',
    whatHappens: 'The white precipitate confirms the formation of calcium carbonate.',
    observe: ['Fine white powder', 'Same compound as limestone', 'Would bubble with acid']
  },
  8: {
    title: 'Reaction 4: NaCl + AgNO₃',
    content: 'Silver chloride (AgCl) is one of the classic insoluble compounds. Ag+ forms insoluble compounds with Cl-, Br-, and I-. AgCl is light-sensitive and turns gray/purple in light.',
    whatHappens: 'A white curdy precipitate of AgCl forms immediately.',
    observe: ['White curdy precipitate', 'AgCl is light-sensitive', 'Classic test for Cl- ions'],
    equation: 'NaCl(aq) + AgNO₃(aq) -> AgCl(s) + NaNO₃(aq)'
  },
  9: {
    title: 'Observing Reaction 4',
    content: 'The white precipitate may darken if exposed to light due to photodecomposition: 2AgCl -> 2Ag + Cl2. This reaction is the basis of traditional photography.',
    whatHappens: 'Confirming the white AgCl precipitate - a characteristic test for chloride ions.',
    observe: ['Dense white precipitate', 'Curdy texture', 'May darken in light']
  },
  10: {
    title: 'Reaction 5: BaCl₂ + Na₂SO₄',
    content: 'Barium sulfate (BaSO₄) is extremely insoluble (Ksp = 1.1 x 10^-10). Despite sulfates generally being soluble, Ba²⁺, Pb²⁺, and Ca²⁺ form insoluble sulfates.',
    whatHappens: 'A fine white precipitate of BaSO₄ forms immediately.',
    observe: ['Fine white precipitate', 'BaSO₄ is extremely insoluble', 'Used in medical X-rays'],
    equation: 'BaCl₂(aq) + Na₂SO₄(aq) -> BaSO₄(s) + 2NaCl(aq)'
  },
  11: {
    title: 'Observing Reaction 5',
    content: 'Barium sulfate is so insoluble and unreactive that it is safely used as a contrast agent in medical imaging, despite barium compounds generally being toxic.',
    whatHappens: 'The very fine white precipitate confirms BaSO₄ formation.',
    observe: ['Very fine white powder', 'Slow to settle', 'Milky suspension']
  },
  12: {
    title: 'Writing Balanced Equations',
    content: 'For each reaction, write the complete ionic equation (showing all ions) and the net ionic equation (showing only the ions that form the precipitate).',
    whatHappens: 'Recording all equations and identifying which products are precipitates (marked with s for solid).',
    observe: ['4 of 5 reactions formed precipitates', 'Solubility rules predict outcomes', 'Net ionic shows actual reaction'],
    equation: 'Net ionic: Cu²⁺(aq) + S2-(aq) -> CuS(s)'
  }
};

export default function doubleDisplacement() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [tubesPrepared, setTubesPrepared] = useState(false);
  const [reaction1Done, setReaction1Done] = useState(false);
  const [reaction1Observed, setReaction1Observed] = useState(false);
  const [reaction2Done, setReaction2Done] = useState(false);
  const [reaction2Observed, setReaction2Observed] = useState(false);
  const [reaction3Done, setReaction3Done] = useState(false);
  const [reaction3Observed, setReaction3Observed] = useState(false);
  const [reaction4Done, setReaction4Done] = useState(false);
  const [reaction4Observed, setReaction4Observed] = useState(false);
  const [reaction5Done, setReaction5Done] = useState(false);
  const [reaction5Observed, setReaction5Observed] = useState(false);
  const [equationsWritten, setEquationsWritten] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeReaction, setActiveReaction] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [
    tubesPrepared, reaction1Done, reaction1Observed, reaction2Done, reaction2Observed,
    reaction3Done, reaction3Observed, reaction4Done, reaction4Observed,
    reaction5Done, reaction5Observed, equationsWritten
  ].filter(Boolean).length;

  const handlePrepareTubes = useCallback(() => {
    if (currentStep === 1 && !tubesPrepared) {
      setTubesPrepared(true);
      setCurrentStep(2);
    }
  }, [currentStep, tubesPrepared]);

  const handleReaction = useCallback((reactionNum: number) => {
    const setDone = [null, setReaction1Done, setReaction2Done, setReaction3Done, setReaction4Done, setReaction5Done][reactionNum];
    const isDone = [null, reaction1Done, reaction2Done, reaction3Done, reaction4Done, reaction5Done][reactionNum];
    const expectedStep = reactionNum * 2;

    if (currentStep === expectedStep && !isDone && setDone) {
      setDone(true);
      setActiveReaction(reactionNum);
      setCurrentStep(expectedStep + 1);
    }
  }, [currentStep, reaction1Done, reaction2Done, reaction3Done, reaction4Done, reaction5Done]);

  const handleObserve = useCallback((reactionNum: number) => {
    const setObserved = [null, setReaction1Observed, setReaction2Observed, setReaction3Observed, setReaction4Observed, setReaction5Observed][reactionNum];
    const isObserved = [null, reaction1Observed, reaction2Observed, reaction3Observed, reaction4Observed, reaction5Observed][reactionNum];
    const expectedStep = reactionNum * 2 + 1;

    if (currentStep === expectedStep && !isObserved && setObserved) {
      setObserved(true);
      if (reactionNum < 5) {
        setCurrentStep(expectedStep + 1);
      } else {
        setCurrentStep(12);
      }
    }
  }, [currentStep, reaction1Observed, reaction2Observed, reaction3Observed, reaction4Observed, reaction5Observed]);

  const handleWriteEquations = useCallback(() => {
    if (currentStep === 12 && reaction5Observed && !equationsWritten) {
      setEquationsWritten(true);
    }
  }, [currentStep, reaction5Observed, equationsWritten]);

  const getReactionState = (reactionNum: number) => {
    const done = [null, reaction1Done, reaction2Done, reaction3Done, reaction4Done, reaction5Done][reactionNum];
    const observed = [null, reaction1Observed, reaction2Observed, reaction3Observed, reaction4Observed, reaction5Observed][reactionNum];
    if (observed) return 'observed';
    if (done) return 'done';
    return 'pending';
  };

  const reactions = doubleDisplacementReactions;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part F: Double Displacement</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare Tubes', hint: 'Set up 5 test tubes', done: tubesPrepared },
            { id: 2, title: 'Rxn 1: NaCl + KNO3', hint: 'Mix solutions', done: reaction1Done },
            { id: 3, title: 'Observe Rxn 1', hint: 'Note: no precipitate', done: reaction1Observed },
            { id: 4, title: 'Rxn 2: CuSO₄ + Na₂S', hint: 'Mix solutions', done: reaction2Done },
            { id: 5, title: 'Observe Rxn 2', hint: 'Note: black CuS', done: reaction2Observed },
            { id: 6, title: 'Rxn 3: CaCl₂ + Na₂CO₃', hint: 'Mix solutions', done: reaction3Done },
            { id: 7, title: 'Observe Rxn 3', hint: 'Note: white CaCO₃', done: reaction3Observed },
            { id: 8, title: 'Rxn 4: NaCl + AgNO₃', hint: 'Mix solutions', done: reaction4Done },
            { id: 9, title: 'Observe Rxn 4', hint: 'Note: white AgCl', done: reaction4Observed },
            { id: 10, title: 'Rxn 5: BaCl₂ + Na₂SO₄', hint: 'Mix solutions', done: reaction5Done },
            { id: 11, title: 'Observe Rxn 5', hint: 'Note: white BaSO₄', done: reaction5Observed },
            { id: 12, title: 'Write Equations', hint: 'Balance all equations', done: equationsWritten }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Double Displacement Reactions"
        completedCount={completedSteps}
        totalSteps={12}
        equipment={['NaCl', 'KNO3', 'CuSO₄', 'Na₂S', 'CaCl₂', 'Na₂CO₃', 'AgNO₃', 'BaCl₂', 'Na₂SO₄']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Test tubes for all 5 reactions */}
            <div className="reaction-tubes">
              {reactions.map((reaction, index) => {
                const reactionNum = index + 1;
                const state = getReactionState(reactionNum);
                const isClickable =
                  (currentStep === reactionNum * 2 && state === 'pending') ||
                  (currentStep === reactionNum * 2 + 1 && state === 'done');

                return (
                  <div
                    key={reaction.id}
                    className={`reaction-tube ${isClickable ? 'clickable' : ''} ${state}`}
                    onClick={() => {
                      if (currentStep === reactionNum * 2) handleReaction(reactionNum);
                      else if (currentStep === reactionNum * 2 + 1) handleObserve(reactionNum);
                    }}
                  >
                    <svg viewBox="0 0 55 120" width={50} height={115}>
                      <defs>
                        <linearGradient id={`tube-dd-${id}-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                          <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                        </linearGradient>
                      </defs>

                      {/* Tube rim */}
                      <ellipse cx="27" cy="12" rx="13" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

                      {/* Tube body */}
                      <path
                        d="M14 12 L14 85 Q14 100 27 100 Q40 100 40 85 L40 12"
                        fill={`url(#tube-dd-${id}-${index})`}
                        stroke="#8BA8C4"
                        strokeWidth="1.5"
                      />

                      {/* Solution - show if reaction done */}
                      {state !== 'pending' && tubesPrepared && (
                        <path
                          d="M16 35 L16 83 Q16 95 27 95 Q38 95 38 83 L38 35 Z"
                          fill={reactionNum === 2 ? saltFormationColors.cuso4 : "rgba(255,255,255,0.7)"}
                        />
                      )}

                      {/* Precipitate if applicable */}
                      {state === 'observed' && reaction.precipitateColor && (
                        <ellipse
                          cx="27"
                          cy="88"
                          rx="10"
                          ry="6"
                          fill={reaction.precipitateColor}
                          stroke={reaction.precipitateColor === '#FFFFFF' || reaction.precipitateColor === '#F5F5F5' || reaction.precipitateColor === '#FAFAFA'
                            ? 'rgba(0,0,0,0.1)' : 'none'}
                          strokeWidth="1"
                        />
                      )}

                      {/* Mixing animation */}
                      {state === 'done' && activeReaction === reactionNum && (
                        <g opacity="0.6">
                          {[...Array(5)].map((_, i) => (
                            <circle
                              key={i}
                              cx={20 + (i % 3) * 7}
                              cy={60}
                              r={2}
                              fill={reactionNum === 2 ? saltFormationColors.cuso4 : '#AAA'}
                            >
                              <animate
                                attributeName="cy"
                                values="70;45;70"
                                dur={`${0.8 + i * 0.1}s`}
                                repeatCount="indefinite"
                              />
                            </circle>
                          ))}
                        </g>
                      )}

                      {/* Glass shine */}
                      <line x1="17" y1="20" x2="17" y2="75" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />

                      {/* Reaction number */}
                      <text x="27" y="110" fontSize="8" fill="#E0E0E0" textAnchor="middle">#{reactionNum}</text>

                      {/* Result indicator */}
                      {state === 'observed' && (
                        <text
                          x="27"
                          y="25"
                          fontSize="5"
                          fill={reaction.precipitateColor ? '#00A86B' : '#888'}
                          textAnchor="middle"
                        >
                          {reaction.precipitateFormula || 'No ppt'}
                        </text>
                      )}
                    </svg>
                  </div>
                );
              })}
            </div>

            {/* Prepare tubes button */}
            {!tubesPrepared && (
              <div
                className={`equipment-station ${currentStep === 1 ? 'clickable' : ''}`}
                onClick={handlePrepareTubes}
              >
                <svg viewBox="0 0 80 100" width={75} height={95}>
                  <rect x="10" y="20" width="60" height="60" fill="#444" stroke="#666" strokeWidth="2" rx="4" />
                  <text x="40" y="55" fontSize="10" fill="#E0E0E0" textAnchor="middle">5 Tubes</text>
                  <text x="40" y="90" fontSize="8" fill="#E0E0E0" textAnchor="middle">Click to set up</text>
                </svg>
              </div>
            )}
          </div>

          {/* Reaction info panel */}
          {activeReaction && (
            <div className="reaction-info">
              <h4>Reaction {activeReaction}</h4>
              <div className="reaction-details">
                <span className="reactants">
                  {reactions[activeReaction - 1].reactant1} + {reactions[activeReaction - 1].reactant2}
                </span>
                <span className="arrow">-&gt;</span>
                <span className="products">
                  {reactions[activeReaction - 1].precipitate || 'No precipitate (all soluble)'}
                </span>
              </div>
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !tubesPrepared && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to set up 5 labeled test tubes for reactions
              </div>
            )}
            {currentStep >= 2 && currentStep <= 10 && currentStep % 2 === 0 && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click tube #{currentStep / 2} to mix the reactants
              </div>
            )}
            {currentStep >= 3 && currentStep <= 11 && currentStep % 2 === 1 && (
              <div className="status-message info">
                <Eye size={20} />
                Click tube #{Math.floor(currentStep / 2)} to observe result:
                {reactions[Math.floor(currentStep / 2) - 1]?.precipitate || ' No precipitate forms'}
              </div>
            )}
            {currentStep === 12 && !equationsWritten && (
              <div
                className="status-message success"
                onClick={handleWriteEquations}
                style={{ cursor: 'pointer' }}
              >
                <FileText size={20} />
                Click to write balanced equations for all reactions
              </div>
            )}
            {equationsWritten && (
              <div className="status-message success">
                <Check size={20} />
                Complete! Precipitates form when products are insoluble.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Double Displacement (Metathesis)</h4>
            <div className="equations">
              {reactions.map((rxn, i) => (
                <div key={i} className={`equation ${getReactionState(i + 1) === 'observed' ? 'revealed' : 'hidden'}`}>
                  {rxn.equation}
                </div>
              ))}
            </div>
            <p>
              In double displacement, cations and anions switch partners.
              A precipitate forms if the new combination is insoluble (check solubility rules).
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            justify-content: center;
          }

          .reaction-tubes {
            display: flex;
            gap: 10px;
            padding: 15px;
            background: rgba(60, 60, 60, 0.3);
            border-radius: 8px;
          }

          .reaction-tube {
            transition: all 0.2s ease;
            border-radius: 4px;
            padding: 5px;
          }

          .reaction-tube.clickable {
            cursor: pointer;
          }

          .reaction-tube.clickable:hover {
            background: rgba(0, 168, 107, 0.2);
          }

          .reaction-tube.observed {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .reaction-info {
            background: var(--bg-elevated);
            padding: 12px 20px;
            border-radius: 8px;
            text-align: center;
          }

          .reaction-info h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .reaction-details {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 12px;
          }

          .reaction-details .reactants {
            color: #60A5FA;
          }

          .reaction-details .arrow {
            color: var(--text-secondary);
          }

          .reaction-details .products {
            color: var(--secondary);
            font-weight: 500;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 550px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .equations {
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            max-height: 100px;
            overflow-y: auto;
          }

          .equation {
            font-family: 'Courier New', monospace;
            padding: 3px 0;
            font-size: 10px;
            transition: all 0.3s ease;
          }

          .equation.hidden {
            color: #444;
          }

          .equation.revealed {
            color: #40E0D0;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
